import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import * as React from 'react';

import { cn } from '@/utils/cn';

const buttonVariants = cva(
    [
        'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md',
        'cursor-pointer select-none',
        'text-sm font-normal',
        'transition-colors',
        'ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
        'disabled:pointer-events-none disabled:opacity-50',
        '[&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0',
    ].join(' '),
    {
        variants: {
            variant: {
                default: 'bg-primary text-primary-foreground hover:bg-primary/90',
                destructive: 'bg-destructive text-destructive-foreground hover:bg-destructive/90',
                outline: 'border border-espresso-200 bg-background hover:bg-seafoam text-espresso-600',
                secondary: 'bg-secondary text-secondary-foreground hover:bg-secondary/80',
                ghost: 'hover:bg-seafoam text-espresso-600',
                link: 'text-primary underline-offset-4 hover:underline',

                ghost2: 'hover:bg-cocoa-100 hover:text-gold-500 text-cocoa-500 rounded-full font-300 font-thin',
                outline2:
                    'border border-border bg-transparent rounded-full hover:bg-white hover:text-gold-500 text-cocoa-600 font-thin',
                highlighted: 'bg-cocoa-800 text-primary-foreground hover:bg-cocoa-700 rounded-full font-thin',
            },
            size: {
                default: 'h-10 px-4 py-2',
                sm: 'h-9 px-3',
                xs: 'h-7 px-3',
                lg: 'h-11 px-8',
                icon: 'h-10 w-10',
            },
        },
        defaultVariants: {
            variant: 'default',
            size: 'default',
        },
    },
);

export interface ButtonProps
    extends React.ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof buttonVariants> {
    asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
    ({ className, variant, size, asChild = false, ...props }, ref) => {
        const Comp = asChild ? Slot : 'button';
        return <Comp className={cn(buttonVariants({ variant, size, className }))} ref={ref} {...props} />;
    },
);
Button.displayName = 'Button';

export { Button, buttonVariants };
